import { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import { useArrivalTimeout } from '@/hooks/useArrivalTimeout';
import { captureError } from '@/lib/observability';
import { userFacingErrorMessage } from '@/utils/userFacingError';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

/**
 * The SCA-challenge machinery that useConsentPolling (AIS) and usePaymentPolling (PIS) share
 * verbatim: which challenge shapes a provider returned, turning a BankID QR *token* into a real
 * image, deciding whether there is anything to poll for, running the poll on the provider's own
 * interval, counting consecutive failures, and bounding "no completion signal, ever".
 *
 * Extracted because the duplication had already cost a real production bug: when the
 * redirect-flow polling fix landed, it went into the consent hook only, and every ZTL payment
 * signed via redirect silently never polled — `needsPolling` stayed false and "All Payments
 * Signed!" never appeared even after the user finished at their bank (see usePaymentPolling's
 * own history). One definition means the next such fix cannot land in one copy and miss the other.
 *
 * What stays with each caller: its own provider call, its own identity gate (`enabled`) and
 * identity key, its own default interval, and its own user-facing copy. Those genuinely differ
 * between AIS and PIS, and folding them in here would hide the difference rather than share it.
 */

const MAX_CONSECUTIVE_ERRORS = 3;

// Issue #386 audit: MAX_CONSECUTIVE_ERRORS only trips on thrown errors. A call that succeeds
// (200, no error) but never reports completion — e.g. the initiation response never included any
// of qrCodeImage/qrToken/bankIdUrl/scaRedirect in the first place, so `needsPolling` never even
// starts an interval — would otherwise have no ceiling at all, leaving the UI on "Setting up
// authentication..." forever. 3 minutes is generous for a user-paced BankID/QR authorization
// (typically well under a minute) but still bounds the wait.
const ARRIVAL_TIMEOUT_MS = 3 * 60 * 1000;

const QR_OPTIONS = {
  width: 300,
  margin: 2,
  color: { dark: '#000000', light: '#FFFFFF' },
} as const;

export interface ScaChallengeInitialData {
  refreshInterval?: number;
  qrToken?: string;
  qrCodeImage?: string;
  bankIdUrl?: string;
  scaRedirect?: string;
}

export interface ScaChallengeData {
  qrCodeImage: string | null;
  qrToken: string | null;
  generatedQrImage: string | null;
  scaStatus: string;
  isCompleted: boolean;
  bankIdUrl?: string;
  scaRedirect?: string;
}

/** What one poll of a provider's authorization status reports back. */
interface ScaStatusUpdate {
  scaStatus: string;
  isCompleted: boolean;
  /** Set by a caller that can tell a terminal provider failure apart from "still pending". */
  isFailed?: boolean;
  /** What to show for this particular failure, over the config's `failedMessage`. */
  failedMessage?: string;
  qrCodeImage?: string;
  qrToken?: string;
  scaRedirect?: string;
  bankIdUrl?: string;
}

export interface ScaChallengePollingConfig {
  /** This caller's own "we have enough identity to poll at all" gate. */
  enabled: boolean;
  /** Fallback when the provider names no refreshInterval and the flow is not a redirect. */
  defaultIntervalMs: number;
  /** "consent" / "payment" — used only in log lines. */
  label: string;
  /** Shown to the user when nothing ever completes. */
  arrivalTimeoutMessage: string;
  /** Sent to observability alongside `arrivalTimeoutContext` when that happens. */
  arrivalTimeoutDescription: string;
  arrivalTimeoutContext: string;
  /** Shown when a poll reports `isFailed`. Required for a caller that sets it. */
  failedMessage?: string;
  /** One poll against the provider. */
  fetchStatus: () => Promise<ScaStatusUpdate>;
  /** The caller's own identity, as one string. The poll restarts when it changes — a single
   *  value rather than a spread array so the dependency stays statically checkable. */
  identityKey: string;
}

/** Renders a BankID QR token as a data-URL image. Returns null if generation fails, so the
 *  caller decides what that means for its own error state. */
async function renderQrToken(token: string): Promise<string | null> {
  try {
    return await QRCode.toDataURL(token, QR_OPTIONS);
  } catch (err) {
    console.error('Failed to generate QR code:', err);
    return null;
  }
}

export function useScaChallengePolling(
  initialData: ScaChallengeInitialData,
  config: ScaChallengePollingConfig,
) {
  const [challenge, setChallenge] = useState<ScaChallengeData>({
    qrCodeImage: initialData.qrCodeImage || null,
    qrToken: initialData.qrToken || null,
    generatedQrImage: null,
    scaStatus: 'unknown',
    isCompleted: false,
    bankIdUrl: initialData.bankIdUrl,
    scaRedirect: initialData.scaRedirect,
  });
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  // The authorization the provider reported as failed. Keyed by identity, so a new authorization
  // (e.g. the switch to same device) is never considered failed and gets its own timeout.
  const [failedIdentityKey, setFailedIdentityKey] = useState<string | null>(null);
  const isFailed = failedIdentityKey === config.identityKey;
  const errorCountRef = useRef(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Config changes on every render (the caller builds it inline), so the poll effect below reads
  // it through a ref rather than depending on it — its real dependency is identityKey.
  const configRef = useRef(config);
  configRef.current = config;
  // Read inside the interval to compare against a rotated token, without making a token change
  // restart the poll.
  const qrTokenRef = useRef(challenge.qrToken);
  qrTokenRef.current = challenge.qrToken;

  // Sync initialData into state when it changes (e.g. after initiation returns a challenge).
  useEffect(() => {
    setChallenge(prev => ({
      ...prev,
      qrCodeImage: initialData.qrCodeImage || prev.qrCodeImage,
      qrToken: initialData.qrToken || prev.qrToken,
      bankIdUrl: initialData.bankIdUrl || prev.bankIdUrl,
      scaRedirect: initialData.scaRedirect || prev.scaRedirect,
    }));
  }, [initialData.qrCodeImage, initialData.qrToken, initialData.bankIdUrl, initialData.scaRedirect]);

  // Generate the QR image from a token, if one arrived and we haven't rendered it yet.
  useEffect(() => {
    (async () => {
      if (!initialData.qrToken || challenge.generatedQrImage) return;
      const dataUrl = await renderQrToken(initialData.qrToken);
      if (dataUrl) setChallenge(prev => ({ ...prev, generatedQrImage: dataUrl }));
      else setError(translateOutsideReact('consultantErrors.fallback.qrGenerate'));
    })();
  }, [initialData.qrToken, challenge.generatedQrImage]);

  useEffect(() => {
    if (challenge.isCompleted) setIsPolling(false);
  }, [challenge.isCompleted]);

  const handleArrivalTimeout = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsPolling(false);
    setError(configRef.current.arrivalTimeoutMessage);
    captureError(
      new Error(`${configRef.current.arrivalTimeoutDescription} within ${ARRIVAL_TIMEOUT_MS}ms`),
      configRef.current.arrivalTimeoutContext,
    );
  }, []);

  useArrivalTimeout(!config.enabled || challenge.isCompleted || isFailed, ARRIVAL_TIMEOUT_MS, handleArrivalTimeout);

  // A redirect flow needs polling just as much as a QR one: the user authenticates in a separate
  // window/tab, so the only way to learn it finished is to ask.
  const isQrFlow = !!(initialData.qrCodeImage || initialData.qrToken || challenge.generatedQrImage);
  const isRedirectFlow = !!initialData.scaRedirect;
  const needsPolling = isQrFlow || isRedirectFlow;

  useEffect(() => {
    const { enabled, label, defaultIntervalMs, fetchStatus } = configRef.current;
    // Redirect flows don't need rapid polling — 2s is enough to detect completion.
    const interval = initialData.refreshInterval || (isRedirectFlow ? 2000 : defaultIntervalMs);

    if (!needsPolling || !enabled) {
      console.debug(`${label} polling: not needed`, needsPolling, enabled);
      return;
    }

    console.log(`Starting ${label} polling every ${interval}ms`);
    setIsPolling(true);

    const pollInterval = setInterval(async () => {
      if (pollIntervalRef.current !== pollInterval) return; // superseded/cleared (e.g. arrival timeout)
      try {
        const status = await fetchStatus();
        errorCountRef.current = 0;

        setChallenge(prev => ({
          ...prev,
          qrCodeImage: status.qrCodeImage || prev.qrCodeImage,
          qrToken: status.qrToken || prev.qrToken,
          scaStatus: status.scaStatus,
          isCompleted: status.isCompleted,
          scaRedirect: status.scaRedirect || prev.scaRedirect,
          bankIdUrl: status.bankIdUrl || prev.bankIdUrl,
        }));

        // Update the generated QR if the token rotated.
        if (status.qrToken && status.qrToken !== qrTokenRef.current) {
          const newQrImage = await renderQrToken(status.qrToken);
          if (newQrImage) setChallenge(prev => ({ ...prev, generatedQrImage: newQrImage }));
          else setError(translateOutsideReact('consultantErrors.fallback.qrUpdate'));
        }

        if (status.isCompleted) {
          console.log(`${label} polling completed`);
          clearInterval(pollInterval);
          setIsPolling(false);
        } else if (status.isFailed) {
          console.log(`${label} polling: provider reported the authorization failed`);
          clearInterval(pollInterval);
          if (pollIntervalRef.current === pollInterval) pollIntervalRef.current = null;
          setIsPolling(false);
          setFailedIdentityKey(configRef.current.identityKey);
          setError(status.failedMessage ?? configRef.current.failedMessage ?? translateOutsideReact('consultantErrors.fallback.authorizationFailed'));
        }
      } catch (err) {
        console.error(`${label} polling error:`, err);
        errorCountRef.current += 1;

        if (errorCountRef.current >= MAX_CONSECUTIVE_ERRORS) {
          console.error(`Stopping polling after ${MAX_CONSECUTIVE_ERRORS} consecutive errors`);
          setError(translateOutsideReact('consultantErrors.fallback.authorizationCheckAttempts', { count: String(MAX_CONSECUTIVE_ERRORS) }));
          clearInterval(pollInterval);
          setIsPolling(false);
        } else {
          setError(userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.bankResponseCheck')));
        }
      }
    }, interval);

    pollIntervalRef.current = pollInterval;

    return () => {
      clearInterval(pollInterval);
      if (pollIntervalRef.current === pollInterval) pollIntervalRef.current = null;
      setIsPolling(false);
      console.log(`Stopped ${label} polling`);
    };
  }, [config.identityKey, initialData.refreshInterval, isRedirectFlow, needsPolling, challenge.generatedQrImage]);

  return {
    ...challenge,
    isFailed,
    error,
    isPolling,
    hasQrCode: !!(challenge.qrCodeImage || challenge.generatedQrImage),
    hasRedirect: !!(challenge.bankIdUrl || challenge.scaRedirect),
    clearError: () => setError(null),
  };
}
